import {
  DestroyRef,
  inject,
  Injectable,
  resource,
  Resource,
  ResourceRef,
  signal,
  Signal,
} from '@angular/core';
import { createOAuthStorage } from '@iwentsourcing/client/oauth/oauth-storage.js';
import { createBrowserEnv } from '@iwentsourcing/browser/create-browser-env';
import { Router } from '@angular/router';
import { createRemoteManagement } from '@iwentsourcing/client/management/create-remote-management.js';
import { createLocalSession } from '@iwentsourcing/browser/create-local-session.js';
import { signalFromDestroyRef } from './signal-from-destroy-ref.js';
import { createConsoleTelemetry } from '@iwentsourcing/core/tracer/create-console-telemetry.js';
import { ApiConfig } from './provide-api.js';
import { PublicAuthProvider } from '@iwentsourcing/core/authentication/public-auth-providers.js';
import { OAuthRemoteInterface } from '@iwentsourcing/client/oauth/o-auth-remote-interface.js';
import { AuthProviders } from '@iwentsourcing/core/authentication/auth-providers.js';
import { RemoteManagement } from '@iwentsourcing/client/management/remote-management.js';
import { UnauthenticatedSession } from '@iwentsourcing/client/oauth/unauthenticated-session.js';
import { AuthenticatedSession } from '@iwentsourcing/client/oauth/authenticated-session.js';
import { createLazy } from '@iwentsourcing/core/lazy/create-lazy.js';
import { createMemoryLock } from '@iwentsourcing/core/lock/create-memory-lock.js';
import type { Lock } from '@iwentsourcing/core/lock/lock.js';

export interface ApiState {
  realmName: string;
  tenant: string;
  // undefined until the providers have been fetched
  authProviders: Signal<Record<string, PublicAuthProvider> | undefined>;
  authProvidersError: Signal<string | null>;
  remote: RemoteManagement<OAuthRemoteInterface<AuthProviders>>;
  session: Signal<AuthenticatedSession | UnauthenticatedSession>;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  destroyRef = inject(DestroyRef);
  router = inject(Router);
  apiConfig = inject(ApiConfig);
  signal = signalFromDestroyRef(this.destroyRef);

  instances: Record<string, ApiState> = {};
  // Refresh locks are per-realm: the JWT lives in localStorage keyed by
  // realm, so every tenant-scoped instance of the same realm must share
  // one lock or they race the single-use refresh token.
  private realmLocks: Record<string, Lock> = {};

  create(tenant: string, realm: string): ApiState {
    const key = `${tenant}:${realm}`;
    const existing = this.instances[key];
    if (existing) {
      return existing;
    }

    console.log({ tenant, realm });
    const session = signal<AuthenticatedSession | UnauthenticatedSession>({
      type: 'unauthenticated',
    });

    const lazyAuthProviders = createLazy(() =>
      this.apiConfig.getProviders(tenant, realm),
    );
    const authProviders = signal<
      Record<string, PublicAuthProvider> | undefined
    >(undefined);
    const authProvidersError = signal<string | null>(null);
    lazyAuthProviders
      .resolve()
      .then((result) => authProviders.set(result))
      .catch((error: unknown) => {
        console.error(error);
        authProvidersError.set(
          error instanceof Error ? error.message : 'Unknown error',
        );
      });
    const realmLock = (this.realmLocks[realm] ??= createMemoryLock());
    const sessionResolver = createOAuthStorage(realm, lazyAuthProviders, {
      sessionStorageFactory: createLocalSession,
      transferStorageFactory: createLocalSession,
      lock: realmLock,
    });

    const remote = createRemoteManagement({
      url: this.apiConfig.host,
      tenant: tenant,
      session: sessionResolver,
      env: createBrowserEnv(),
      signal: this.signal,
      telemetry: createConsoleTelemetry(),
    });

    remote.session.on(async (evt) => {
      if (evt.type === 'error') {
        console.error(evt.error);
        session.set({ type: 'unauthenticated' });
        await remote.session.logout();
      } else if (evt.type === 'unauthenticated') {
        console.log('navigate to login');
        session.set({ type: 'unauthenticated' });
      } else {
        session.set(evt);
      }
    }, this.signal);

    const newInstance: ApiState = {
      remote,
      realmName: realm,
      authProviders,
      authProvidersError,
      tenant,
      session,
    };

    this.instances[key] = newInstance;

    return newInstance;
  }
}
